import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Form,
  Modal,
  Select,
  Input,
  Table,
  Button,
  Space,
  Row,
  Col,
  notification,
  Radio
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { ReactComponent as IconClose } from 'assets/images/close.svg';
import { splitTransactions } from 'services/api/module/booking.api';
import useModal from 'stores/useModal';
import QUERY_KEYS from 'services/api/queryKeys';
import { queryClient } from 'index';
import { useTranslation } from 'react-i18next';
import { useGetTransactionDetail } from 'hooks/useGetGuestTransactions';
import { getColumnSplitTransactions } from 'constants/table';
import { FOLIO_BALANCE_CODE } from 'services/api/type/booking.type';

interface TransactionLine {
  key: string;
  transaction_id: number;
  transaction_name: string;
  transaction_date: string;
  transaction_amount: number;
  transaction_code_id: number;
  transaction_qty: number;
  transaction_note: string;
  folio_balance_code: string;
  partner_id: number;
  partner_name: string;
  guest_id: number;
  split_amount?: number;
  split_percent?: number;
  split_quantity?: number;
  remain_amount?: number;
  remain_quantity?: number;
  original_available_amount?: number;
  original_available_quantity?: number;
}

// Utility functions for calculations
const calculateTotalAmount = (line: TransactionLine): number => {
  return line.transaction_amount * line.transaction_qty;
};

const calculateSplitPercent = (splitAmount: number, totalAmount: number): number => {
  return totalAmount > 0 ? (splitAmount / totalAmount) * 100 : 0;
};

const validateSplitValue = (
  value: number,
  availableValue: number,
  type: 'amount' | 'quantity'
): boolean => {
  if (value > availableValue) {
    const message = `Split ${type} (${value.toLocaleString()}) cannot exceed the available ${type} (${availableValue.toLocaleString()})`;
    notification.warning({ message });
    return false;
  }
  return true;
};

const createNewTransactionLine = (
  baseTransaction: any,
  lastLine: TransactionLine
): TransactionLine => {
  return {
    key: `new-${Date.now()}`,
    transaction_id: baseTransaction.transaction_id,
    transaction_name: baseTransaction.transaction_name,
    transaction_date: baseTransaction.transaction_date,
    transaction_amount: baseTransaction.transaction_amount,
    transaction_code_id: baseTransaction.transaction_code_id,
    transaction_qty: 1.0,
    transaction_note: baseTransaction.transaction_note,
    folio_balance_code: baseTransaction.folio_balance_code,
    partner_id: baseTransaction.partner_id,
    partner_name: baseTransaction.partner_name,
    guest_id: baseTransaction.guest_id,
    split_amount: 0,
    split_percent: 0,
    split_quantity: 0,
    remain_amount: lastLine.remain_amount,
    remain_quantity: lastLine.remain_quantity || 0,
    original_available_amount: lastLine.remain_amount,
    original_available_quantity: lastLine.remain_quantity || 0
  };
};

function SplitTransactions() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [transactionLines, setTransactionLines] = useState<TransactionLine[]>([]);
  const [splitMethod, setSplitMethod] = useState<string>('amount');

  const {
    splitTransactions: { isOpenSplitTransactions, transaction_ids },
    closeSplitTransactions
  } = useModal();
  const { data: transactionDetail } = useGetTransactionDetail(transaction_ids);
  const currentQuantity = transactionDetail?.transaction_qty || 1;
  const splitQuantity = transactionLines.reduce(
    (acc, line) => acc + (line?.split_quantity || 0),
    0
  );

  const { mutateAsync: splitTransactionsMutation } = useMutation({
    mutationFn: (params: any) =>
      splitTransactions({
        guest_id: params.guest_id,
        params: params.params
      })
  });

  useEffect(() => {
    if (transactionDetail) {
      form.setFieldsValue({
        transactionPage: transactionDetail.folio_balance_code,
        guestName: transactionDetail.partner_name
      });

      const initialLine: TransactionLine = {
        key: transactionDetail.transaction_id.toString(),
        ...transactionDetail,
        split_amount: transactionDetail.transaction_amount * transactionDetail.transaction_qty,
        split_percent: 100,
        split_quantity: transactionDetail.transaction_qty,
        remain_amount: 0,
        remain_quantity: 0,
        original_available_amount:
          transactionDetail.transaction_amount * transactionDetail.transaction_qty,
        original_available_quantity: transactionDetail.transaction_qty
      };
      setTransactionLines([initialLine]);
    }
  }, [form, transactionDetail]);

  const handleDateChange = (date: any, recordKey: string) => {
    const updatedLines = transactionLines.map(line =>
      line.key === recordKey
        ? { ...line, transaction_date: date ? date.format('YYYY-MM-DD') : line.transaction_date }
        : line
    );
    setTransactionLines(updatedLines);
  };

  // Generic handler for split value changes (amount, percent, quantity)
  const handleSplitChange = (
    value: number,
    recordKey: string,
    type: 'amount' | 'percent' | 'quantity'
  ) => {
    const currentLine = transactionLines.find(line => line.key === recordKey);
    if (!currentLine) return;

    const currentIndex = transactionLines.findIndex(line => line.key === recordKey);
    const isNewLine = currentLine.key.startsWith('new-');
    const previousLine = currentIndex > 0 ? transactionLines[currentIndex - 1] : null;

    // Validation for new lines
    if (isNewLine && previousLine) {
      const availableAmount = previousLine.remain_amount || 0;
      const availableQuantity = previousLine.remain_quantity || 0;

      let validationValue = value;
      if (type === 'percent') {
        const totalAmount = currentLine.original_available_amount || 0;
        validationValue = (value / 100) * totalAmount;
      }

      const validationType = type === 'quantity' ? 'quantity' : 'amount';
      const availableValue = type === 'quantity' ? availableQuantity : availableAmount;

      if (!validateSplitValue(validationValue, availableValue, validationType)) {
        return;
      }
    }

    const updatedLines = transactionLines.map(line => {
      if (line.key !== recordKey) return line;

      const totalAmount = calculateTotalAmount(line);
      const originalAmount = line.original_available_amount || totalAmount;

      let updates: Partial<TransactionLine> = {};

      switch (type) {
        case 'amount':
          updates = {
            split_amount: value,
            split_percent: calculateSplitPercent(value, isNewLine ? originalAmount : totalAmount)
          };
          break;

        case 'percent':
          const splitAmount = (value / 100) * (isNewLine ? originalAmount : totalAmount);
          updates = {
            split_percent: value,
            split_amount: splitAmount
          };
          break;

        case 'quantity':
          updates = { split_quantity: value };
          break;
      }

      // Calculate remaining values
      if (isNewLine && previousLine) {
        const availableAmount = previousLine.remain_amount || 0;
        const availableQuantity = previousLine.remain_quantity || 0;

        updates.remain_amount =
          type === 'quantity' ? availableAmount : availableAmount - (updates.split_amount || 0);
        updates.remain_quantity =
          type === 'quantity' ? availableQuantity - value : availableQuantity;
      } else {
        // Original line calculations
        const totalQty = line.transaction_qty;
        updates.remain_amount =
          type === 'quantity' ? line.remain_amount : totalAmount - (updates.split_amount || 0);
        updates.remain_quantity = type === 'quantity' ? totalQty - value : line.remain_quantity;
      }

      return { ...line, ...updates };
    });

    setTransactionLines(recalculateRemainingLines(updatedLines));
  };

  // Specific handlers that call the generic handler
  const handleSplitAmountChange = (value: number, recordKey: string) => {
    handleSplitChange(value, recordKey, 'amount');
  };

  const handleSplitPercentChange = (value: number, recordKey: string) => {
    handleSplitChange(value, recordKey, 'percent');
  };

  const handleSplitQuantityChange = (value: number, recordKey: string) => {
    handleSplitChange(value, recordKey, 'quantity');
  };

  // Simplified recalculation logic
  const recalculateRemainingLines = (lines: TransactionLine[]): TransactionLine[] => {
    return lines.map((line, index) => {
      if (index === 0) {
        // First line: calculate remaining from original transaction
        const totalAmount = calculateTotalAmount(line);
        return {
          ...line,
          remain_amount: totalAmount - (line.split_amount || 0),
          remain_quantity: line.transaction_qty - (line.split_quantity || 0)
        };
      }

      const previousLine = lines[index - 1];
      const availableAmount = previousLine.remain_amount || 0;
      const availableQuantity = previousLine.remain_quantity || 0;
      const currentSplitAmount = line.split_amount || 0;
      const currentSplitQuantity = line.split_quantity || 0;

      const updatedLine = {
        ...line,
        remain_amount: Math.max(0, availableAmount - currentSplitAmount),
        remain_quantity: Math.max(0, availableQuantity - currentSplitQuantity)
      };

      // Set original available values if not already set
      if (!updatedLine.original_available_amount) {
        updatedLine.original_available_amount = availableAmount;
      }
      if (!updatedLine.original_available_quantity) {
        updatedLine.original_available_quantity = availableQuantity;
      }

      // Calculate split percent for new lines
      if (line.key.startsWith('new-') && updatedLine.original_available_amount > 0) {
        updatedLine.split_percent = calculateSplitPercent(
          currentSplitAmount,
          updatedLine.original_available_amount
        );
      }

      return updatedLine;
    });
  };

  const addTransactionLine = () => {
    if (!transactionDetail) return;

    const lastLine = transactionLines[transactionLines.length - 1];
    const hasRemainingAmount = lastLine?.remain_amount && lastLine.remain_amount > 0;
    const canAddQuantityLine = splitQuantity < currentQuantity;

    if (!hasRemainingAmount && currentQuantity === 1) {
      notification.error({
        message: 'No remaining amount available to split. Cannot add more transaction lines.'
      });
      return;
    }

    if (!canAddQuantityLine && currentQuantity > 1) {
      notification.error({
        message: 'No remaining quantity available to split. Cannot add more transaction lines.'
      });
      return;
    }

    const newLine = createNewTransactionLine(transactionDetail, lastLine);
    setTransactionLines(recalculateRemainingLines([...transactionLines, newLine]));
  };

  const removeTransactionLine = (recordKey: string) => {
    if (transactionLines.length <= 1) {
      notification.warning({ message: 'At least one transaction line is required' });
      return;
    }

    const updatedLines = transactionLines.filter(line => line.key !== recordKey);
    setTransactionLines(recalculateRemainingLines(updatedLines));
  };

  const handleCloseSplitTransactions = () => {
    closeSplitTransactions();
    form.resetFields();
    setTransactionLines([]);
  };

  // Prepare split data for API submission
  const prepareSplitData = (lines: TransactionLine[]) => {
    return lines.map(line => ({
      transaction_id: line.key.startsWith('new-') ? undefined : line.transaction_id,
      amount: currentQuantity > 1 ? line.transaction_amount : line.split_amount || 0,
      percentage: currentQuantity > 1 ? 0 : line.split_percent || 0,
      qty: currentQuantity > 1 ? line.split_quantity || line.transaction_qty : line.transaction_qty,
      transaction_date: line.transaction_date,
      transaction_code_id: line.transaction_code_id,
      note: line.transaction_note || ''
    }));
  };

  // Invalidate relevant queries after successful split
  const invalidateQueries = async () => {
    const queries = [
      [QUERY_KEYS.GET_GUEST_TRANSACTIONS, transactionDetail?.guest_id],
      [QUERY_KEYS.GET_GUEST],
      [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL],
      [QUERY_KEYS.GET_TRANSACTION_HISTORY_LOGS]
    ];

    await Promise.all(queries.map(queryKey => queryClient.invalidateQueries({ queryKey })));
  };

  const handleFormSubmit = async (values: any) => {
    try {
      await splitTransactionsMutation({
        guest_id: transactionDetail?.guest_id || 0,
        params: {
          transaction_id: transactionDetail?.transaction_id || 0,
          folio_balance_code: values.transactionPage,
          split_data: prepareSplitData(transactionLines)
        }
      });

      await invalidateQueries();
      notification.success({ message: t('common.message.splitTransactionSuccess') });
      handleCloseSplitTransactions();
    } catch (err: any) {
      notification.error({ message: err.error || t('common.error') });
    }
  };

  const columns = getColumnSplitTransactions(
    currentQuantity,
    handleDateChange,
    removeTransactionLine,
    splitMethod,
    handleSplitPercentChange,
    handleSplitAmountChange,
    handleSplitQuantityChange
  );

  return (
    <Modal
      title="SPLIT TRANSACTION"
      centered
      width={1400}
      open={isOpenSplitTransactions}
      onCancel={handleCloseSplitTransactions}
      closeIcon={<IconClose />}
      onOk={() => form.submit()}
      cancelText={t('common.actions.cancel')}
      okText={t('common.actions.save')}
      okButtonProps={{ className: 'ant-btn-secondary' }}
      className="modal-split-transactions"
      destroyOnClose
    >
      <Form
        form={form}
        layout="horizontal"
        onFinish={handleFormSubmit}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        initialValues={{
          transactionPage: 'A',
          guestName: transactionDetail?.partner_name || '',
          splitMethod: 'amount'
        }}
      >
        <div style={{ marginBottom: '20px' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col>
                <p style={{ margin: 0, fontWeight: 'bold', marginTop: 5 }}>
                  Split To Transaction Page <span style={{ color: 'red' }}>(*)</span>
                </p>
              </Col>
              <Col>
                <Form.Item
                  name="transactionPage"
                  rules={[{ required: true, message: 'Please select a transaction page!' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select style={{ width: 120 }} options={FOLIO_BALANCE_CODE} />
                </Form.Item>
              </Col>

              <Col style={{ marginLeft: 16 }}>
                <p style={{ margin: 0, fontWeight: 'bold', marginTop: 5 }}>
                  Guest name <span style={{ color: 'red' }}>(*)</span>
                </p>
              </Col>
              <Col>
                <Form.Item
                  name="guestName"
                  rules={[
                    { required: true, message: 'Please enter guest name!' },
                    { min: 2, message: 'Guest name must be at least 2 characters!' }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Enter guest name" style={{ width: 200 }} />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Form.Item name="splitMethod" style={{ marginBottom: 0 }}>
                  <Radio.Group
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    onChange={e => {
                      setSplitMethod(e.target.value);
                      form.setFieldValue('splitMethod', e.target.value);
                    }}
                    disabled={currentQuantity > 1}
                  >
                    <Radio value="amount" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                      By Amount
                    </Radio>
                    <Radio value="percent" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                      By Percent
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </div>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addTransactionLine}
                style={{ marginLeft: 'auto' }}
              >
                Add Transaction Line
              </Button>
            </div>
          </Space>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <Table
            columns={columns}
            dataSource={transactionLines}
            pagination={false}
            size="small"
            scroll={{ y: 400, x: 1000 }}
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              height: '400px'
            }}
          />
        </div>
      </Form>
    </Modal>
  );
}

export default SplitTransactions;
